import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "Booked"];

const Leads = () => {
    const { user, userLoading } = useAuth();
    const [leads, setLeads] = useState(null);
    const [query, setQuery] = useState("");
    const [examFilter, setExamFilter] = useState("All exams");
    const [savingRow, setSavingRow] = useState("");

    useEffect(() => {
        if (user?.role === "admin") {
            fetch(`${API}/admin/leads`, { credentials: "include" })
                .then((r) => (r.ok ? r.json() : { leads: [] }))
                .then((d) => setLeads(d.leads || []))
                .catch(() => setLeads([]));
        }
    }, [user]);

    const exams = useMemo(() => {
        const set = new Set((leads || []).map((l) => l.exam).filter(Boolean));
        return ["All exams", ...Array.from(set)];
    }, [leads]);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (leads || []).filter((l) => {
            const matchesExam = examFilter === "All exams" || l.exam === examFilter;
            if (!matchesExam) return false;
            if (!q) return true;
            return [l.name, l.phone, l.exam, l.slot, l.note, l.source, l.status]
                .join(" ")
                .toLowerCase()
                .includes(q);
        });
    }, [leads, query, examFilter]);

    const changeStatus = async (lead, status) => {
        setSavingRow(lead.id);
        try {
            const res = await fetch(`${API}/admin/leads/${lead.id}/status`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ status }),
            });
            if (res.ok) {
                setLeads((all) =>
                    (all || []).map((l) => (l.id === lead.id ? { ...l, status } : l))
                );
            }
        } finally {
            setSavingRow("");
        }
    };

    const downloadCsv = () => {
        const header = ["Received", "Name", "WhatsApp", "Exam", "Slot", "Status", "Source", "Note"];
        const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
        const lines = [header.join(",")];
        visible.forEach((l) => {
            lines.push(
                [
                    l.created_at ? String(l.created_at).slice(0, 16).replace("T", " ") : "",
                    l.name,
                    l.phone,
                    l.exam,
                    l.slot,
                    l.status || "New",
                    l.source || "Book form",
                    l.note,
                ]
                    .map(esc)
                    .join(",")
            );
        });
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "panel-room-leads.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    if (userLoading) {
        return (
            <main className="py-32 text-center" data-testid="leads-loading">
                <p className="text-sm text-ink-muted">Checking your seat…</p>
            </main>
        );
    }

    if (!user || user.role !== "admin") {
        return (
            <main className="py-32 text-center" data-testid="leads-gate">
                <p className="eyebrow">Leads</p>
                <h2 className="mt-4 font-display text-3xl tracking-tight sm:text-4xl">
                    Admin access <em className="italic text-brass">only.</em>
                </h2>
                <p className="mx-auto mt-4 max-w-md text-sm text-ink-muted">
                    This page is private. Sign in with the admin password to view leads.
                </p>
                <Link to="/login" className="btn-primary mt-8" data-testid="leads-signin-link">
                    Sign in
                </Link>
            </main>
        );
    }

    return (
        <main className="py-20 md:py-28" data-testid="leads-page">
            <div className="wrap">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="eyebrow">Leads</p>
                        <h2 className="mt-4 text-3xl leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                            Everyone <em className="italic text-brass">knocking.</em>
                        </h2>
                    </div>
                    <Link
                        to="/studio"
                        data-testid="leads-back-to-studio"
                        className="u-link text-sm tracking-wide text-ink transition-colors duration-300 hover:text-brass"
                    >
                        Back to AI Studio
                    </Link>
                </div>

                <div className="mt-10 grid gap-4 sm:grid-cols-[1fr_auto_auto] sm:items-end">
                    <div>
                        <label htmlFor="leads-search" className="field-label">
                            Search
                        </label>
                        <input
                            id="leads-search"
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search name, number, exam, note…"
                            data-testid="leads-search"
                            className="field"
                        />
                    </div>
                    <div>
                        <label htmlFor="leads-exam-filter" className="field-label">
                            Exam
                        </label>
                        <select
                            id="leads-exam-filter"
                            value={examFilter}
                            onChange={(e) => setExamFilter(e.target.value)}
                            data-testid="leads-exam-filter"
                            className="field"
                        >
                            {exams.map((e) => (
                                <option key={e} value={e}>
                                    {e}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        onClick={downloadCsv}
                        data-testid="leads-download-csv"
                        className="btn-ghost inline-flex items-center gap-2"
                    >
                        <Download size={14} strokeWidth={1.5} />
                        Download CSV
                    </button>
                </div>

                <div className="mt-8 border border-line bg-surface p-4 sm:p-8" data-testid="leads-table-wrap">
                    {leads === null ? (
                        <p className="py-6 text-sm text-ink-muted">Loading…</p>
                    ) : visible.length === 0 ? (
                        <p className="py-6 text-sm text-ink-muted" data-testid="leads-empty">
                            No leads match. {leads.length > 0 ? "Adjust the search or exam filter." : "New submissions will appear here."}
                        </p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[860px] text-left text-sm">
                                <thead>
                                    <tr className="border-b border-line text-[11px] uppercase tracking-[0.16em] text-ink-muted">
                                        <th className="py-3 pr-4">Received</th>
                                        <th className="py-3 pr-4">Name</th>
                                        <th className="py-3 pr-4">WhatsApp</th>
                                        <th className="py-3 pr-4">Exam</th>
                                        <th className="py-3 pr-4">Slot / Note</th>
                                        <th className="py-3 pr-4">Status</th>
                                        <th className="py-3">Source</th>
                                    </tr>
                                </thead>
                                <tbody data-testid="leads-table">
                                    {visible.map((l, i) => (
                                        <tr key={l.id || i} className="border-b border-line/60 last:border-none">
                                            <td className="py-3 pr-4 text-xs text-ink-muted tabular-nums">
                                                {l.created_at ? String(l.created_at).slice(0, 16).replace("T", " ") : "—"}
                                            </td>
                                            <td className="py-3 pr-4">{l.name}</td>
                                            <td className="py-3 pr-4 tabular-nums">{l.phone}</td>
                                            <td className="py-3 pr-4">{l.exam}</td>
                                            <td className="py-3 pr-4">
                                                {l.note ? <span className="text-ink">{l.note}</span> : l.slot}
                                            </td>
                                            <td className="py-3 pr-4">
                                                <select
                                                    value={STATUSES.includes(l.status) ? l.status : "New"}
                                                    onChange={(e) => changeStatus(l, e.target.value)}
                                    disabled={savingRow === l.id}
                                                    data-testid={`lead-status-${l.id}`}
                                                    aria-label={`Status for ${l.name}`}
                                                    className="border border-line bg-paper px-2 py-1.5 text-xs text-ink outline-none transition-colors duration-300 focus:border-brass"
                                                >
                                                    {STATUSES.map((s) => (
                                                        <option key={s} value={s}>
                                                            {s}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="py-3">
                                                <span
                                                    data-testid={`lead-source-${l.id}`}
                                                    className="border border-brass px-2 py-0.5 text-[10px] uppercase tracking-widest text-brass"
                                                >
                                                    {l.source || "Book form"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default Leads;
